import { toLocalDateString, toLocalTimeString } from './localDateTime';

/** A wall-clock reading: the civil date (`YYYY-MM-DD`) and the time (`HH:mm`). */
export interface WallClock {
  date: string;
  time: string;
}

/**
 * What the clocks in `timeZone` (IANA, such as a hospital's "Africa/Luanda") read at `now`. Intl applies the zone's
 * offset and daylight-saving rule for that instant. Without a zone, or with one this browser does not know, it is the
 * browser's own clock.
 */
export function wallClockIn(timeZone: string | null | undefined, now: Date = new Date()): WallClock {
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      });
      const parts = new Map(formatter.formatToParts(now).map((part) => [part.type, part.value]));
      return { date: `${parts.get('year')}-${parts.get('month')}-${parts.get('day')}`, time: `${parts.get('hour')}:${parts.get('minute')}` };
    } catch (error) {
      console.warn(`Unknown time zone "${timeZone}"; using the browser's clock`, error);
    }
  }
  return { date: toLocalDateString(now), time: toLocalTimeString(now) };
}

/** The civil date `days` calendar days after `date` (`YYYY-MM-DD`), with no clock or time zone involved. */
export function addCivilDays(date: string, days: number): string {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  const civil = new Date(0);
  // Unlike Date.UTC, setUTCFullYear keeps years 0 to 99 as written instead of moving them to the 1900s.
  civil.setUTCFullYear(year, month - 1, day + days);
  return civil.toISOString().slice(0, 10);
}

/**
 * Formats a wall-clock reading stored as a UTC-encoded ISO string (`…T09:00:00.000Z` meaning 09:00 at the hospital,
 * as schedule exceptions are kept) with no zone shift: 09:00 shows as 09:00 whatever the browser's time zone.
 */
export function formatStoredWallClock(iso: string, bcp47Locale: string, options: Intl.DateTimeFormatOptions = {}): string {
  return new Intl.DateTimeFormat(bcp47Locale, { ...options, timeZone: 'UTC' }).format(new Date(iso));
}
